/**
 * Environment-based Feature Flags
 *
 * Feature flags that are configured via environment variables.
 * Uses the env adapter for cross-platform compatibility.
 *
 * Part of Batch 6 migration.
 */

import { getEnv } from '../adapters'

/**
 * Parse a value as boolean
 * Handles various truthy string representations
 * @param {any} value - Value to parse
 * @returns {boolean} - Parsed boolean value
 */
export const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value !== 'string') return false
  const normalized = value.trim().toLowerCase()
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on'
}

/**
 * Get a feature flag from environment
 * @param {string} key - Primary env key
 * @param {string} legacyKey - Optional legacy/fallback env key
 * @param {boolean} defaultValue - Default value if not set
 * @returns {boolean} - Feature flag value
 */
export const getFeatureFlag = (key, legacyKey = null, defaultValue = false) => {
  const value = getEnv(key) ?? (legacyKey ? getEnv(legacyKey) : null)
  if (value === null || value === undefined) return defaultValue
  return parseBoolean(value)
}

/**
 * Payments feature flag
 * Env: FEATURE_PAYMENTS_ENABLED or ENABLE_ONLINE_PAYMENTS (legacy)
 */
export const isPaymentsEnabled = () => {
  return getFeatureFlag('FEATURE_PAYMENTS_ENABLED', 'ENABLE_ONLINE_PAYMENTS', false)
}

/**
 * Messaging feature flag
 * Env: FEATURE_MESSAGING_ENABLED
 */
export const isMessagingEnabled = () => {
  return getFeatureFlag('FEATURE_MESSAGING_ENABLED', null, false)
}

/**
 * Get all environment-based feature flags
 * @returns {object} - Object with all env feature flags
 */
export const getEnvFeatureFlags = () => ({
  payments: isPaymentsEnabled(),
  messaging: isMessagingEnabled(),
})

// Legacy exports for backward compatibility
export const FEATURE_PAYMENTS_ENABLED = isPaymentsEnabled()
export const FEATURE_MESSAGING_ENABLED = isMessagingEnabled()
export const getFeatureFlags = getEnvFeatureFlags
